import React from "react";
import { Helmet } from "react-helmet-async";
import {
  getSeoForPath,
  MAIN_WEBSITE_TITLE,
  SITE_NAME,
  DEFAULT_OG_IMAGE,
} from "utils/seoRoutes";

const SITE_URL = "https://golfusion.id";

/**
 * Renders per-route <title>/meta tags on the client.
 * Mounted once in MainContainer so every route gets correct SEO
 * automatically based on src/utils/seoRoutes.js.
 *
 * Note: social crawlers (WhatsApp/Facebook) don't execute JS, so they
 * always see the static defaults baked into public/index.html (homepage
 * content). This component is what Googlebot (which does render JS) and
 * the browser tab see, and what keeps document.title correct per page.
 *
 * `pathname` is passed down from App.js (via the router's subscribe API)
 * rather than read with useLocation(), because this component is mounted
 * outside the <RouterProvider> tree and has no Router context.
 */
function SEO({ title, description, image, pathname }) {
  const route = getSeoForPath(pathname || "/");

  const resolvedTitle = title || route.title;
  const resolvedDescription = description || route.description;
  const resolvedImage = image
    ? image
    : SITE_URL + DEFAULT_OG_IMAGE;
  const fullTitle = MAIN_WEBSITE_TITLE + resolvedTitle;
  const canonicalUrl = SITE_URL + (pathname || "/");
  const isHome = (pathname || "/") === "/";

  const eventJsonLd = {
    "@context": "https://schema.org",
    "@type": "SportsEvent",
    name: "Golfusion Golf Tournament 2026",
    description: resolvedDescription,
    startDate: "2026-08-29T06:30:00+07:00",
    eventStatus: "https://schema.org/EventScheduled",
    eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
    location: {
      "@type": "Place",
      name: "Sentul Highland Golf Club",
      address: {
        "@type": "PostalAddress",
        addressCountry: "ID",
      },
    },
    image: [resolvedImage],
    organizer: {
      "@type": "Organization",
      name: SITE_NAME,
      url: SITE_URL,
    },
  };

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={resolvedDescription} />
      <link rel="canonical" href={canonicalUrl} />

      <meta property="og:type" content="website" />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={resolvedDescription} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={resolvedImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={resolvedDescription} />
      <meta name="twitter:image" content={resolvedImage} />

      {isHome && (
        <script type="application/ld+json">
          {JSON.stringify(eventJsonLd)}
        </script>
      )}
    </Helmet>
  );
}

export default SEO;
